import { CloseCircleFilled, DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Tag, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { projectApi } from "api/project.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { useDebounce } from "hooks/useDebounce";
import { useProject } from "hooks/useProject";
import { useEffect, useMemo, useRef, useState } from "react";
import { userStore } from "store/userStore";
import { Project } from "types/project";
import { getTitle } from "utils";
import { checkRole } from "utils/auth";
import { $url } from "utils/url";
import { ProjectModal } from "views/Project/components/ProjectModal";

const viewProjectPath = "chi-tiet-du-an";
const deleteProjectPath = "xoa-du-an";
const editProjectPath = "chinh-sua-du-an";

export const ProjectPage = ({ title = "" }) => {
  const [search, setSearch] = useState("");

  const projectModalRef = useRef<ProjectModal>(null);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const { Projects, totalProject, fetchProject, loadingProject, setQueryProject, queryProject } = useProject({
    initQuery: {
      page: 1,
      limit: 50,
      search: "",
    },
  });

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setQueryProject((prev) => ({ ...prev, search: debouncedSearch, page: 1 }));
  }, [debouncedSearch]);

  useEffect(() => {
    fetchProject();
  }, [queryProject]);

  const handleDeleteProject = async (id: number) => {
    try {
      await projectApi.delete(id);
      message.success("Xóa thành công");
      fetchProject();
    } catch (error) {
      console.error(error);
    }
  };

  const canViewProject = checkRole(viewProjectPath, userStore.info.role?.permissions);
  const canEditProject = checkRole(editProjectPath, userStore.info.role?.permissions);
  const canDeleteProject = checkRole(deleteProjectPath, userStore.info.role?.permissions);
  const isAdminProject = (userStore.info.projects as Project[])?.length === 0;

  const columns = useMemo<TableColumnsType<Project>>(
    () => [
      {
        title: "Tên dự án",
        dataIndex: "name",
        key: "name",
        width: 200,
        render: (_, record: Project) => (
          <div className="space-y-1 min-w-0">
            <INA image={record.logo ? $url(record.logo) : undefined} name={record.name || "--"} />
          </div>
        ),
      },

      {
        title: "Tenant ID",
        dataIndex: "tenantId",
        key: "tenantId",
        width: 150,
        render: (value: string) => <TableContent>{value || "--"}</TableContent>,
      },

      {
        title: "Hệ thống prompt",
        dataIndex: "systemPrompt",
        key: "systemPrompt",
        width: 350,
        render: (value: string) => <div className="line-clamp-1">{value || "--"}</div>,
      },

      ...(canDeleteProject || canEditProject || canViewProject
        ? [
            {
              title: "Thao tác",
              key: "action",
              width: 160,
              align: "center" as const,
              render: (_: any, record: Project) => (
                <div className="flex gap-2 justify-center items-center">
                  {canEditProject && (
                    <Tooltip title="Cập nhật">
                      <ActionButton
                        aria-label="Cập nhật"
                        onClick={() => {
                          projectModalRef.current?.handleUpdate(record);
                        }}
                      >
                        <EditOutlined style={{ color: "#6c84e5" }} />
                      </ActionButton>
                    </Tooltip>
                  )}
                  {canViewProject && (
                    <Tooltip title="Xem chi tiết">
                      <ActionButton
                        aria-label="Xem chi tiết"
                        onClick={() => {
                          projectModalRef.current?.handleView(record);
                        }}
                      >
                        <EyeOutlined style={{ color: "#6c84e5" }} />
                      </ActionButton>
                    </Tooltip>
                  )}
                  {canDeleteProject && (
                    <Tooltip title="Xóa">
                      <Popconfirm
                        cancelText="Không"
                        okText="Xóa"
                        onConfirm={() => handleDeleteProject(record.id)}
                        title="Xác nhận xóa?"
                      >
                        <ActionButton aria-label="Xóa">
                          <DeleteOutlined style={{ color: "#ef4444" }} />
                        </ActionButton>
                      </Popconfirm>
                    </Tooltip>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canDeleteProject, canEditProject],
  );

  const { showingFrom, showingTo } = getTableShowingRange(
    queryProject.page,
    queryProject.limit,
    totalProject,
  );

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm tên dự án",
              onChange: (value) => {
                setSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQueryProject((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              canEditProject &&
              isAdminProject && (
                <AppButton
                  tone="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    projectModalRef.current?.handleCreate();
                  }}
                >
                  Tạo mới
                </AppButton>
              )
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Project>
            rowKey="id"
            loading={loadingProject}
            pagination={false}
            dataSource={Projects}
            columns={columns}
            scroll={{ x: 1100 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={totalProject}
            currentPage={queryProject.page}
            pageSize={queryProject.limit}
            onChange={({ limit, page }) => {
              setQueryProject((prev) => ({ ...prev, page, limit }));
            }}
          />
        }
      />

      <ProjectModal ref={projectModalRef} onSubmitOk={() => fetchProject()} onClose={() => {}} />
    </div>
  );
};
